import React, { useState, useContext, useEffect } from "react";
import { login } from "../api/auth";
import { AuthContext } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { fetchUserByUUID } from "../api/users";
import { jwtDecode } from "jwt-decode";
import logoSmall from "../assets/logo_orange.svg";
import { PLATFORM_COMPANY_ID } from "../constants/companies";
import { useTranslation } from "react-i18next";

export function LoginForm() {
  const [numisId, setId] = useState("");
  const [pw, setPw] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const {
    token,
    userId,
    company,
    companyRole,
    setToken,
    setUserId,
    setCompany,
    setCompanyRole,
  } = useContext(AuthContext);
  const nav = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!token || !userId) {
      return;
    }

    const existingUser = {
      user_id: userId,
      company,
      company_role: companyRole,
    };
    const nextRoute = resolveHomeRoute(existingUser);
    nav(nextRoute, { replace: true });
  }, [token, userId, company, companyRole, nav]);

  const submit = async (e) => {
    e.preventDefault();

    try {
      if (showPassword) {
        setShowPassword(false);
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      const resp = await login(numisId, pw);
      const token = resp.data.access_token;
      setToken(token);

      const decoded = jwtDecode(token);
      const jwtUserId = decoded.sub;

      const { data: user } = await fetchUserByUUID(jwtUserId);
      if (!user) throw new Error("User fetch failed.");
      setUserId(user.user_id);
      setCompany(user.company || null);
      setCompanyRole(user.company_role || null);
      // Post-login navigation is handled by the authenticated-state effect.
    } catch (err) {
      const backendKey = err.response?.data?.message_key || err.response?.data?.message?.message_key;
      const backendMessage = err.response?.data?.message?.message || err.response?.data?.message;
      window.alert(
        t(backendKey || "errors.loginFailed", {
          defaultValue: backendMessage || t("errors.loginFailed"),
        })
      );
    }
};

function resolveHomeRoute(user) {
  if (isPlatformAdmin(user)) {
    return "/admin";
  }

  const normalizedRole = (user.company_role || "").toLowerCase();
  if (canCertify(user)) {
    return "/cert";
  }

  if (normalizedRole === "reports") {
    return "/verify";
  }

  return "/owner";
}

function isPlatformAdmin(user) {
  if (!user || !user.company) return false;
  const normalizedRole = (user.company_role || "").toLowerCase();
  return user.company.company_id === PLATFORM_COMPANY_ID && normalizedRole === "admin";
}

function canCertify(user) {
  const company = user.company;
  if (!company) return false;
  const normalizedRole = (user.company_role || "").toLowerCase();
  const trustedCompany = company.trusted_for_certification || ["CERTIFICATION_ENTITY", "PLATFORM"].includes(company.company_type);
  return ["admin", "expert"].includes(normalizedRole) && trustedCompany;
}

  return (
    <div className="login-screen">
      <div className="login-brand">
        <img alt={t("login.logoAlt")} className="login-logo" src={logoSmall} />
        <h1>{t("app.title") + "\u2122"}</h1>
        <p className="login-tagline">{t("app.tagline")}</p>
      </div>

      <form className="login-card" onSubmit={submit} method="post" autoComplete="on">
        <div className="login-field">
          <label htmlFor="numisId">{t("login.numisIdLabel")}</label>
          <input
            id="numisId"
            name="username"
            placeholder={t("login.numisIdPlaceholder")}
            autoComplete="username"
            value={numisId}
            onChange={(e) => setId(e.target.value)}
            required
          />
        </div>

        <div className="login-field">
          <label htmlFor="password">{t("login.passwordLabel")}</label>
          <div className="login-password-input">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder={t("login.passwordPlaceholder")}
              autoComplete="current-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              required
            />
            <button
              type="button"
              className="login-toggle"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
            >
              {showPassword ? "🙈" : "👁️"}
            </button>
          </div>
        </div>

        <button className="login-submit" type="submit">
          {t("login.submit")}
        </button>

        <div className="login-links">
          <a href="/register" className="login-link-primary">{t("login.registerLink")}</a>
          <a href="/forgot-password" className="login-link-secondary">{t("login.forgotPassword")}</a>
        </div>
      </form>

      <div className="legal-footer">
        <p className="legal-note">{t("legal.brandNotice")}</p>
        <div className="legal-links">
          <a href="/terms" target="_blank" rel="noopener noreferrer">
            {t("legal.terms")}
          </a>
          <span className="legal-links__sep" aria-hidden="true">·</span>
          <a href="/privacy" target="_blank" rel="noopener noreferrer">
            {t("legal.privacy")}
          </a>
          <span className="legal-links__sep" aria-hidden="true">·</span>
          <a href="/disclaimer" target="_blank" rel="noopener noreferrer">
            {t("legal.disclaimer")}
          </a>
        </div>
      </div>
    </div>
  );
}
